import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import CommonHeadingNBtns from "../common-components/CommonHeadingNBtns";
import { commonPostPutApi } from "../../redux/reducers/commonApiSlice";
import useCloudFrontUpload from "../../hooks/useCloudFrontUpload";
import { add_investor, update_investor } from "../../data/ApiEndpoints";
import { investorSchema } from "../../schema/website-schema";
import "../../styles/website/management-forms.css";

const ManageInvestor = ({
  closeModal,
  token,
  initialValues,
  isEdit,
  getInvestorData,
}) => {
  const dispatch = useDispatch();
  const { uploadToCloudfront, isUploading } = useCloudFrontUpload();
  const [isLoading, setIsLoading] = useState(false);

  // Process initial values
  const processedInitialValues = () => {
    if (!initialValues) {
      console.log("No initial values provided, returning defaults");
      return {
        name: "",
        investor_type: "",
        email: "",
        profile: "",
        seq_no: "",
        company_name: "",
        extra: "",
      };
    }

    // Return processed initial values
    const processedValues = {
      ...initialValues,
      company_name: initialValues.company_name || "",
      extra: initialValues.extra || "",
    };

    return processedValues;
  };

  const formik = useFormik({
    initialValues: processedInitialValues(),
    validationSchema: investorSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        // Determine method and endpoint
        const method = isEdit ? "PUT" : "POST";
        let endpoint;

        if (isEdit && initialValues?.uid) {
          endpoint = `${update_investor}/${initialValues.uid}`;
        } else {
          endpoint = add_investor;
        }

        // Prepare payload based on edit/add mode
        const payload = { ...values };

        // Add UID for edit operations
        if (isEdit && initialValues?.uid) {
          payload.uid = initialValues.uid;
          console.log("Added UID to payload:", payload.uid);
        }

        console.log("Final API payload:", payload);

        // Remove empty values
        Object.keys(payload).forEach((key) => {
          if (payload[key] === undefined || payload[key] === null) {
            delete payload[key];
          }
        });

        try {
          const result = await dispatch(
            commonPostPutApi({ method, endpoint, payload, token })
          );

          console.log("API response:", result);

          if (result?.payload?.status) {
            toast.success(result?.payload?.message || "Success!");
            formik.resetForm();
            getInvestorData && getInvestorData();
            closeModal();
          } else {
            const errorMessage =
              result?.error?.message || result?.payload?.message;
            toast.error(errorMessage || "Something Went Wrong!");
          }
        } catch (apiError) {
          console.error("API Error:", apiError);
          toast.error(
            "API request failed: " + (apiError.message || "Unknown error")
          );
        }
      } catch (error) {
        console.error("General Error in Submit:", error);
        toast.error("Something went wrong!");
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Add debugging for formik values
  useEffect(() => {
    console.log("Current formik values:", formik.values);
  }, [formik.values]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        setIsLoading(true);
        console.log("Uploading image to Cloudfront...");
        const imageUrl = await uploadToCloudfront(file, token);
        if (imageUrl) {
          console.log("Image uploaded successfully:", imageUrl);
          formik.setFieldValue("profile", imageUrl);
        } else {
          toast.error("Failed to upload profile image");
        }
      } catch (error) {
        console.error("Upload error:", error);
        toast.error("Image upload failed");
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Independent API submission function
  const submitToApi = async (values) => {
    setIsLoading(true);

    try {
      // Determine method and endpoint
      const method = isEdit ? "PUT" : "POST";
      let endpoint;

      if (isEdit && initialValues?.uid) {
        endpoint = `${update_investor}/${initialValues.uid}`;
      } else {
        endpoint = add_investor;
      }

      // Prepare payload
      const payload = { ...values };

      // Add UID for edit operations
      if (isEdit && initialValues?.uid) {
        payload.uid = initialValues.uid;
        console.log("Added UID to payload:", payload.uid);
      }

      console.log("Final API payload:", payload);

      // Remove empty values
      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined || payload[key] === null) {
          delete payload[key];
        }
      });

      // Make API call
      // console.log("Making API call directly:", {
      //   method,
      //   endpoint,
      //   payload,
      //   hasToken: !!token,
      // });

      const result = await dispatch(
        commonPostPutApi({ method, endpoint, payload, token })
      );

      console.log("API response:", result);

      if (result?.payload?.status) {
        toast.success(result?.payload?.message || "Success!");
        formik.resetForm();
        getInvestorData && getInvestorData();
        closeModal();
      } else {
        const errorMessage = result?.error?.message || result?.payload?.message;
        toast.error(errorMessage || "Something Went Wrong!");
      }
    } catch (error) {
      console.error("API submission error:", error);
      toast.error("Something went wrong!");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {
    if (e) e.preventDefault();

    // Touch all fields to show all validation errors immediately
    const touchedFields = {};
    Object.keys(formik.values).forEach((key) => {
      touchedFields[key] = true;
    });
    formik.setTouched(touchedFields);

    // Validate form before submission
    const errors = await formik.validateForm();

    if (Object.keys(errors).length === 0) {
      await submitToApi(formik.values);
    } else {
      // Create error message listing all validation issues
      const errorMessages = Object.entries(errors).map(([field, message]) => {
        const fieldName =
          field.charAt(0).toUpperCase() + field.slice(1).replace("_", " ");
        return `${fieldName}: ${message}`;
      });

      // Show toast with the first error
      toast.error(`Please fix these errors: ${errorMessages[0]}`);

      // Scroll to the first error field
      const firstErrorField = document.getElementById(Object.keys(errors)[0]);
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
        firstErrorField.focus();
      }
    }
  };

  return (
    <div className="createNewCTOModalContainer mod-width-50p">
      <CommonHeadingNBtns
        title={isEdit ? "Update Investor" : "Add Investor"}
        onClose={closeModal}
        btnTxt={
          isLoading || isUploading ? "Processing..." : isEdit ? "Update" : "Add"
        }
        onClick={handleFormSubmit}
        isDisabled={isLoading || isUploading}
      />

      <form
        className="management-form-container"
        onSubmit={(e) => {
          e.preventDefault();
          console.log("Form onSubmit event triggered");
          handleFormSubmit(e);
        }}
      >
        <div className="management-form-elements form-grid form-grid-2">
          {/* Investor Name */}
          <div className="management-form-group">
            <label
              htmlFor="name"
              className={`management-form-label ${
                formik.touched.name && formik.errors.name ? "error" : ""
              }`}
            >
              Investor Name <span className="required-indicator">*</span>
            </label>
            <input
              id="name"
              type="text"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter investor name"
              className={`management-form-input ${
                formik.touched.name && formik.errors.name ? "error" : ""
              }`}
            />
            {formik.touched.name && formik.errors.name && (
              <div className="management-form-error">{formik.errors.name}</div>
            )}
          </div>

          {/* Email */}
          <div className="management-form-group">
            <label
              htmlFor="email"
              className={`management-form-label ${
                formik.touched.email && formik.errors.email ? "error" : ""
              }`}
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter email address"
              className={`management-form-input management-email-input ${
                formik.touched.email && formik.errors.email ? "error" : ""
              }`}
            />
            {formik.touched.email && formik.errors.email && (
              <div className="management-form-error">{formik.errors.email}</div>
            )}
          </div>

          {/* Investor Type */}
          <div className="management-form-group">
            <label
              htmlFor="investor_type"
              className={`management-form-label ${
                formik.touched.investor_type && formik.errors.investor_type
                  ? "error"
                  : ""
              }`}
            >
              Investor Type
            </label>
            <input
              id="investor_type"
              name="investor_type"
              type="text"
              value={formik.values.investor_type}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter investor type"
              className={`management-form-input ${
                formik.touched.investor_type && formik.errors.investor_type
                  ? "error"
                  : ""
              }`}
            />
            {formik.touched.investor_type && formik.errors.investor_type && (
              <div className="management-form-error">
                {formik.errors.investor_type}
              </div>
            )}
          </div>

          {/* Company Name */}
          <div className="management-form-group">
            <label
              htmlFor="company_name"
              className={`management-form-label ${
                formik.touched.company_name && formik.errors.company_name
                  ? "error"
                  : ""
              }`}
            >
              Company Name
            </label>
            <input
              id="company_name"
              type="text"
              name="company_name"
              value={formik.values.company_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter company name"
              className={`management-form-input ${
                formik.touched.company_name && formik.errors.company_name
                  ? "error"
                  : ""
              }`}
            />
            {formik.touched.company_name && formik.errors.company_name && (
              <div className="management-form-error">
                {formik.errors.company_name}
              </div>
            )}
          </div>

          {/* Sequence Number */}
          <div className="management-form-group">
            <label
              htmlFor="seq_no"
              className={`management-form-label ${
                formik.touched.seq_no && formik.errors.seq_no ? "error" : ""
              }`}
            >
              Sequence Number <span className="required-indicator">*</span>
            </label>
            <input
              id="seq_no"
              type="number"
              name="seq_no"
              value={formik.values.seq_no}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter sequence number"
              className={`management-form-input ${
                formik.touched.seq_no && formik.errors.seq_no ? "error" : ""
              }`}
            />
            {formik.touched.seq_no && formik.errors.seq_no && (
              <div className="management-form-error">
                {formik.errors.seq_no}
              </div>
            )}
          </div>

          <br />

          {/* Extra Field */}
          <div className="management-form-group">
            <label
              htmlFor="extra"
              className={`management-form-label ${
                formik.touched.extra && formik.errors.extra ? "error" : ""
              }`}
            >
              Extra Information
            </label>
            <textarea
              id="extra"
              name="extra"
              value={formik.values.extra}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter extra information"
              rows="4"
              className={`management-form-textarea ${
                formik.touched.extra && formik.errors.extra ? "error" : ""
              }`}
            />
            {formik.touched.extra && formik.errors.extra && (
              <div className="management-form-error">{formik.errors.extra}</div>
            )}
          </div>

          {/* Profile Image - Moved to bottom */}
          <div className="management-form-group">
            <label
              htmlFor="profile"
              className={`management-form-label ${
                formik.touched.profile && formik.errors.profile ? "error" : ""
              }`}
            >
              Profile Image <span className="required-indicator">*</span>
            </label>
            <div className="management-image-upload">
              {formik.values.profile && (
                <div className="management-image-preview">
                  <img src={formik.values.profile} alt="Profile Preview" />
                </div>
              )}
              <div className="management-image-upload-inputs">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ marginBottom: "5px" }}
                />
                <input
                  id="profile"
                  type="text"
                  name="profile"
                  value={formik.values.profile}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Or enter image URL"
                  className={`management-form-input ${
                    formik.touched.profile && formik.errors.profile
                      ? "error"
                      : ""
                  }`}
                />
              </div>
            </div>
            {formik.touched.profile && formik.errors.profile && (
              <div className="management-form-error">
                {formik.errors.profile}
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default ManageInvestor;
